import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { FREE_SHIPPING_THRESHOLD_CENTS } from './constants';
const CartContext = createContext(undefined);
export function CartProvider({ children }) {
    const [items, setItems] = useState([]);
    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
            try {
                setItems(JSON.parse(savedCart));
            }
            catch (error) {
                console.error('Failed to load cart:', error);
            }
        }
    }, []);
    // Save cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(items));
    }, [items]);
    const addItem = (product, quantity = 1) => {
        setItems((prevItems) => {
            const existingItem = prevItems.find((item) => item.id === product.id);
            if (existingItem) {
                return prevItems.map((item) => item.id === product.id
                    ? { ...item, quantity: item.quantity + quantity }
                    : item);
            }
            return [
                ...prevItems,
                {
                    id: product.id,
                    name: product.name,
                    priceCents: product.priceCents,
                    shippingCostCents: product.shippingCostCents || 0,
                    currency: product.currency,
                    imageUrl: product.imageUrls?.[0] || product.imageUrl || '',
                    sizeLabel: product.sizeLabel,
                    quantity,
                },
            ];
        });
    };
    const removeItem = (productId) => {
        setItems((prevItems) => prevItems.filter((item) => item.id !== productId));
    };
    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeItem(productId);
            return;
        }
        setItems((prevItems) => prevItems.map((item) => item.id === productId ? { ...item, quantity } : item));
    };
    const clearCart = () => {
        setItems([]);
    };
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const subtotalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
    const rawShippingCents = items.reduce((sum, item) => sum + (item.shippingCostCents || 0) * item.quantity, 0);
    const isFreeShipping = subtotalCents >= FREE_SHIPPING_THRESHOLD_CENTS;
    const shippingCents = isFreeShipping ? 0 : rawShippingCents;
    const totalCents = subtotalCents + shippingCents;
    return (_jsx(CartContext.Provider, { value: {
            items,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            totalItems,
            totalCents,
            subtotalCents,
            shippingCents,
            isFreeShipping,
            freeShippingThreshold: FREE_SHIPPING_THRESHOLD_CENTS,
        }, children: children }));
}
export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
}
